// components/settings/surfox-settings/sections/IntegrationsSection.jsx
// Integrations - Connect SurFox to CRMs and automation tools

import React, { useState, useEffect } from 'react';
import {
  Plug,
  Zap,
  ExternalLink,
  CheckCircle,
  Plus,
  Trash2,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  Users,
  ChevronDown,
  Check,
  Loader2,
  Tag,
  X,
  Download
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import supabase from '../../../../lib/supabaseClient';
import RecruiterFlowImport from '../../integrations/RecruiterFlowImport';
import ApiKeyManager from './ApiKeyManager';

// ============================================================================
// INTEGRATION CARD COMPONENT
// ============================================================================

const IntegrationCard = ({ 
  name, 
  logo, 
  description, 
  status, 
  onConnect, 
  onManage,
  comingSoon 
}) => (
  <div className={`
    bg-white rounded-xl border p-4 flex items-center justify-between
    ${comingSoon ? 'border-slate-100 opacity-60' : 'border-slate-200 hover:border-slate-300'}
    transition-colors
  `}>
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
        {logo}
      </div>
      <div>
        <div className="flex items-center space-x-2">
          <h4 className="font-semibold text-slate-900">{name}</h4>
          {status === 'connected' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Connected
            </span>
          )}
          {comingSoon && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
              Coming Soon
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>
    </div>
    
    {!comingSoon && (
      <button
        onClick={status === 'connected' ? onManage : onConnect}
        className={`
          px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0
          ${status === 'connected' 
            ? 'text-slate-600 bg-slate-100 hover:bg-slate-200' 
            : 'text-white bg-slate-900 hover:bg-slate-800'
          }
        `}
      >
        {status === 'connected' ? 'Manage' : 'Connect'}
      </button>
    )}
  </div>
);

// ============================================================================
// ZAPIER WORKFLOW EMBED COMPONENT
// ============================================================================

const ZapierWorkflowEmbed = ({ userId, fallbackEmail }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('users_profile')
          .select('first_name, last_name, email')
          .eq('id', userId)
          .single();

        if (!error && data) {
          setProfile(data);
        } else {
          setProfile({ email: fallbackEmail, first_name: '', last_name: '' });
        }
      } catch {
        setProfile({ email: fallbackEmail, first_name: '', last_name: '' });
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
    }
  }, [userId, fallbackEmail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full" style={{ minHeight: '600px' }}>
      {/* Downloadable quickstart guide (PDF) — lives in public/howto/ */}
      <a
        href="/howto/SurFox_Zapier_Quickstart.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors group"
      >
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Download className="w-4 h-4 text-orange-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">Zapier quickstart guide (PDF)</p>
            <p className="text-xs text-slate-500">Step-by-step: build your Zap and get instant hot-lead alerts in about 5 minutes</p>
          </div>
        </div>
        <span className="text-xs font-medium text-orange-700 group-hover:underline flex-shrink-0 ml-3">Open</span>
      </a>

      <zapier-workflow
        sign-up-email={profile?.email || fallbackEmail || ''}
        sign-up-first-name={profile?.first_name || ''}
        sign-up-last-name={profile?.last_name || ''}
        client-id="tDMGnqrZ8ICBPOyMq0QSqg131E1X1NXYxZEVF8Ky"
        theme="light"
        intro-copy-display="show"
        guess-zap-display="show"
      />
    </div>
  );
};

// ============================================================================
// ZAPIER CONNECTION MODAL
// ============================================================================

const ZapierSetupModal = ({ isOpen, onClose, onSave, existingConnections }) => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [connectionName, setConnectionName] = useState('');
  const [selectedTriggers, setSelectedTriggers] = useState(['lead_turned_hot']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showWebhookUrl, setShowWebhookUrl] = useState(false);

  const AVAILABLE_TRIGGERS = [
    { id: 'lead_turned_hot', label: 'Lead Turned Hot', description: 'When a lead reaches "hot" status and is ready for contact' },
    { id: 'appointment_booked', label: 'Appointment Booked', description: 'When a calendar booking is completed' },
    { id: 'lead_opted_out', label: 'Lead Opted Out', description: 'When a lead unsubscribes from messages' },
  ];

  const handleSave = async () => {
    setError('');
    
    if (!webhookUrl) {
      setError('Please enter a Zapier webhook URL');
      return;
    }
    
    if (!webhookUrl.includes('hooks.zapier.com')) {
      setError('Please enter a valid Zapier webhook URL (should contain hooks.zapier.com)');
      return;
    }

    if (selectedTriggers.length === 0) {
      setError('Please select at least one trigger');
      return;
    }

    setSaving(true);
    
    try {
      await onSave({
        webhook_url: webhookUrl,
        integration_name: connectionName || 'Zapier Connection',
        subscribed_events: selectedTriggers
      });
      
      setWebhookUrl('');
      setConnectionName('');
      setSelectedTriggers(['lead_turned_hot']);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Connect Zapier</h3>
              <p className="text-sm text-slate-500">Send hot leads to 5,000+ apps</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1 */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center">1</span>
              <h4 className="font-medium text-slate-900">Create a Zap in Zapier</h4>
            </div>
            <div className="ml-8 p-4 bg-slate-50 rounded-lg">
              <ol className="text-sm text-slate-600 space-y-2">
                <li>1. Go to <a href="https://zapier.com/app/zaps" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">zapier.com</a> and create a new Zap</li>
                <li>2. Choose <strong>"Webhooks by Zapier"</strong> as your trigger</li>
                <li>3. Select <strong>"Catch Hook"</strong></li>
                <li>4. Copy the webhook URL Zapier gives you</li>
              </ol>
            </div>
          </div>

          {/* Step 2 */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center">2</span>
              <h4 className="font-medium text-slate-900">Paste your webhook URL</h4>
            </div>
            <div className="ml-8 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Connection Name (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., Hot Leads to Bullhorn"
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Zapier Webhook URL <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showWebhookUrl ? 'text' : 'password'}
                    placeholder="https://hooks.zapier.com/hooks/catch/..."
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebhookUrl(!showWebhookUrl)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showWebhookUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center">3</span>
              <h4 className="font-medium text-slate-900">Choose what triggers the Zap</h4>
            </div>
            <div className="ml-8 space-y-2">
              {AVAILABLE_TRIGGERS.map((trigger) => (
                <label 
                  key={trigger.id} 
                  className={`
                    flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${selectedTriggers.includes(trigger.id) 
                      ? 'border-orange-300 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={selectedTriggers.includes(trigger.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTriggers([...selectedTriggers, trigger.id]);
                      } else {
                        setSelectedTriggers(selectedTriggers.filter(t => t !== trigger.id));
                      }
                    }}
                    className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                  />
                  <div>
                    <span className="font-medium text-slate-900 text-sm">{trigger.label}</span>
                    <p className="text-xs text-slate-500 mt-0.5">{trigger.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Connecting...' : 'Connect Zapier'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// RECRUITERFLOW SETUP MODAL
// ============================================================================

const RecruiterFlowSetupModal = ({ isOpen, onClose, onSave, existingConnection }) => {
  const [apiKey, setApiKey] = useState('');
  const [connectionName, setConnectionName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (existingConnection) {
      setConnectionName(existingConnection.integration_name || '');
      // Don't pre-fill API key for security
    }
  }, [existingConnection]);

  const handleSave = async () => {
    setError('');
    
    if (!apiKey) {
      setError('Please enter your RecruiterFlow API key');
      return;
    }

    setSaving(true);
    
    try {
      await onSave({
        api_key: apiKey,
        integration_name: connectionName || 'RecruiterFlow Connection',
      });
      
      setApiKey('');
      setConnectionName('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Connect RecruiterFlow</h3>
              <p className="text-sm text-slate-500">Sync hot leads directly to your ATS</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* How it works */}
          <div className="p-4 bg-sky-50 border border-sky-100 rounded-lg">
            <h4 className="font-medium text-sky-900 mb-2">What happens when a lead turns hot:</h4>
            <ul className="text-sm text-sky-800 space-y-1">
              <li>• SurFox searches RecruiterFlow by phone number</li>
              <li>• If found → Updates candidate + adds activity note</li>
              <li>• If not found → Creates new candidate</li>
              <li>• Activity note includes score, AI reasoning, and conversation summary</li>
            </ul>
          </div>

          {/* Get API Key Instructions */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center">1</span>
              <h4 className="font-medium text-slate-900">Get your API key from RecruiterFlow</h4>
            </div>
            <div className="ml-8 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Contact RecruiterFlow support or your account manager to request an API key. 
                They'll provide a key that looks like a long string of letters and numbers.
              </p>
              <a 
                href="https://help.recruiterflow.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-sky-600 hover:underline mt-2"
              >
                RecruiterFlow Help Center
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </div>
          </div>

          {/* Enter API Key */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center">2</span>
              <h4 className="font-medium text-slate-900">Enter your credentials</h4>
            </div>
            <div className="ml-8 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Connection Name (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g., HealthPlus RecruiterFlow"
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  RecruiterFlow API Key <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="Enter your API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Your API key is stored securely and encrypted.
                </p>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Connecting...' : existingConnection ? 'Update Connection' : 'Connect RecruiterFlow'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MANAGE CONNECTIONS MODAL
// ============================================================================

const ManageConnectionsModal = ({ isOpen, onClose, connections, onDelete, onToggle, integrationType, title }) => {
  const [deleting, setDeleting] = useState(null);
  const handleDelete = async (connectionId) => {
    setDeleting(connectionId);
    try {
      await onDelete(connectionId);
    } finally {
      setDeleting(null);
    }
  };

  if (!isOpen) return null;

  const filteredConnections = connections.filter(c => c.integration_type === integrationType);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">View and manage your connections</p>
        </div>

        <div className="p-6">
          {filteredConnections.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No connections yet</p>
          ) : (
            <div className="space-y-3">
              {filteredConnections.map((conn) => (
                <div 
                  key={conn.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-slate-900 truncate">{conn.integration_name}</h4>
                      {conn.enabled ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                          Paused
                        </span>
                      )}
                    </div>
                    {conn.subscribed_events && (
                      <p className="text-xs text-slate-500 mt-1">
                        Triggers: {conn.subscribed_events?.join(', ') || 'None'}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">
                      Created {new Date(conn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => onToggle(conn.id, !conn.enabled)}
                      className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200"
                      title={conn.enabled ? 'Pause' : 'Resume'}
                    >
                      {conn.enabled ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(conn.id)}
                      disabled={deleting === conn.id}
                      className="p-2 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HIGHLEVEL MAPPINGS MODAL
// ============================================================================

const HighLevelMappingsModal = ({
  isOpen,
  onClose,
  mappings,
  campaigns,
  hasApiKey,
  inboundUrl,
  onAdd,
  onUpdateCampaign,
  onSoftDelete,
}) => {
  const activeMappings = mappings.filter((m) => m.is_active);
  const inactiveMappings = mappings.filter((m) => !m.is_active);

  const [setupExpanded, setSetupExpanded] = useState(activeMappings.length === 0);
  const [showInactive, setShowInactive] = useState(false);
  const [addingMode, setAddingMode] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newCampaignId, setNewCampaignId] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Auto-collapse setup once the user has any active mappings
  useEffect(() => {
    setSetupExpanded(activeMappings.length === 0);
  }, [activeMappings.length]);

  if (!isOpen) return null;

  const handleCopyField = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmitAdd = async () => {
    if (!newTag.trim()) {
      setAddError('Tag is required');
      return;
    }
    if (!newCampaignId) {
      setAddError('Pick a campaign');
      return;
    }
    setAddError('');
    setAdding(true);
    const result = await onAdd(newTag, newCampaignId);
    setAdding(false);
    if (result?.error) {
      setAddError(result.error);
      return;
    }
    setNewTag('');
    setNewCampaignId('');
    setAddingMode(false);
  };

  const handleCancelAdd = () => {
    setNewTag('');
    setNewCampaignId('');
    setAddError('');
    setAddingMode(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <span className="text-sm font-bold text-teal-700">HL</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">HighLevel Integration</h3>
              <p className="text-sm text-slate-500">Route inbound leads from HighLevel Workflows by tag</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* SETUP SECTION */}
          <div>
            {activeMappings.length > 0 ? (
              <button
                onClick={() => setSetupExpanded((prev) => !prev)}
                className="flex items-center space-x-2 text-sm font-medium text-slate-600 hover:text-slate-900 mb-3"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${setupExpanded ? '' : '-rotate-90'}`} />
                <span>Setup details</span>
              </button>
            ) : (
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Setup</h4>
            )}

            {setupExpanded && (
              <div className="space-y-3">
                {/* Step 1: Webhook URL */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 mb-2">Webhook URL</p>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-700 break-all">
                          {inboundUrl}
                        </code>
                        <button
                          onClick={() => handleCopyField('url', inboundUrl)}
                          className="p-2 text-slate-400 hover:text-teal-600 rounded-lg hover:bg-teal-50 transition-colors flex-shrink-0"
                          title={copiedField === 'url' ? 'Copied!' : 'Copy webhook URL'}
                        >
                          {copiedField === 'url' ? (
                            <Check className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Paste this into the HighLevel Workflow webhook action.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 2: API key */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 mb-1">API key header</p>
                      {hasApiKey ? (
                        <p className="text-sm text-slate-600">
                          Use any active key from your <strong>API Keys</strong> section. Add it as the{' '}
                          <code className="px-1 py-0.5 bg-white border border-slate-200 rounded text-xs font-mono">
                            X-SurFox-API-Key
                          </code>{' '}
                          header on the HighLevel webhook.
                        </p>
                      ) : (
                        <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-1">
                          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700">
                            You don't have any API keys yet. Close this modal and generate one in the{' '}
                            <strong>API Keys</strong> section above.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 3: Map tags */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 mb-1">Map HighLevel tags to campaigns</p>
                      <p className="text-sm text-slate-600">
                        Add a mapping for each HighLevel tag your workflow sends in the{' '}
                        <code className="px-1 py-0.5 bg-white border border-slate-200 rounded text-xs font-mono">
                          tag
                        </code>{' '}
                        field. Tagged leads route to the matching SurFox campaign.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* TAG MAPPINGS SECTION */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Tag mappings</h4>
              {!addingMode && (
                <button
                  onClick={() => setAddingMode(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add mapping</span>
                </button>
              )}
            </div>

            {/* Inline add row */}
            {addingMode && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg mb-3">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-5">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tag</label>
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="e.g., hot-lead"
                      autoFocus
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-5">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Campaign</label>
                    <select
                      value={newCampaignId}
                      onChange={(e) => setNewCampaignId(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                    >
                      <option value="">Pick a campaign…</option>
                      {campaigns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 flex items-end space-x-1">
                    <button
                      onClick={handleSubmitAdd}
                      disabled={adding}
                      className="flex-1 p-2 text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 transition-colors"
                      title="Save"
                    >
                      {adding ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        <Check className="w-4 h-4 mx-auto" />
                      )}
                    </button>
                    <button
                      onClick={handleCancelAdd}
                      disabled={adding}
                      className="flex-1 p-2 text-slate-600 bg-white border border-slate-300 hover:bg-slate-100 rounded-lg disabled:opacity-50 transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>
                {addError && (
                  <div className="mt-2 flex items-center space-x-2 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{addError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Empty state */}
            {activeMappings.length === 0 && !addingMode && (
              <div className="text-center py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl">
                <Tag className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-1">No mappings yet</p>
                <p className="text-xs text-slate-400">
                  Add your first tag mapping to start routing HighLevel leads to SurFox AI campaigns.
                </p>
              </div>
            )}

            {/* Mappings table */}
            {(activeMappings.length > 0 || (showInactive && inactiveMappings.length > 0)) && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600">Tag</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600">Campaign</th>
                      <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                      <th className="text-right px-4 py-2.5 font-medium text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeMappings.map((m) => {
                      const campaignKnown = !!campaigns.find((c) => c.id === m.campaign_id);
                      return (
                        <tr key={m.id} className="border-b border-slate-100 last:border-0">
                          <td className="px-4 py-3">
                            <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-700">
                              {m.tag}
                            </code>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={m.campaign_id}
                              onChange={(e) => onUpdateCampaign(m.id, e.target.value)}
                              className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              {!campaignKnown && (
                                <option value={m.campaign_id} disabled>
                                  (unknown campaign)
                                </option>
                              )}
                              {campaigns.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Active
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => onSoftDelete(m.id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                              title="Remove mapping"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {showInactive &&
                      inactiveMappings.map((m) => {
                        const campaign = campaigns.find((c) => c.id === m.campaign_id);
                        return (
                          <tr
                            key={m.id}
                            className="border-b border-slate-100 last:border-0 opacity-50"
                          >
                            <td className="px-4 py-3">
                              <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono text-slate-500">
                                {m.tag}
                              </code>
                            </td>
                            <td className="px-4 py-3 text-slate-500">{campaign?.name || '—'}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                                Inactive
                              </span>
                            </td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Show inactive toggle */}
            {inactiveMappings.length > 0 && (
              <button
                onClick={() => setShowInactive((prev) => !prev)}
                className="mt-3 text-xs text-slate-500 hover:text-slate-700"
              >
                {showInactive
                  ? `Hide inactive (${inactiveMappings.length})`
                  : `Show inactive (${inactiveMappings.length})`}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// FOLLOW UP BOSS MODAL — map FUB stages to SurFox campaigns (+ hot write-back)
// ============================================================================
// Unlike HighLevel (tags), FUB routing is by STAGE: moving a person into a
// mapped stage in FUB hands them to SurFox on the matching campaign. Saving goes
// through /api/integrations so the backend auto-registers the FUB webhook.
async function apiFetch(path, options = {}) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const res = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });
  if (res.status === 204) return null;
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

const FollowUpBossModal = ({ isOpen, onClose, connection, campaigns, hasApiKey, onSave }) => {
  const isEdit = !!connection;
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [hotStage, setHotStage] = useState('');
  const [rows, setRows] = useState([{ stage: '', campaign_id: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setApiKey('');
    setShowKey(false);
    setError('');
    setWarning('');
    const cfg = connection?.config || {};
    setHotStage(cfg.fub_hot_stage || '');
    const existing = Array.isArray(cfg.fub_stage_mappings) ? cfg.fub_stage_mappings : [];
    setRows(existing.length
      ? existing.map((m) => ({ stage: m.stage || '', campaign_id: m.campaign_id || '' }))
      : [{ stage: '', campaign_id: '' }]);
  }, [isOpen, connection]);

  if (!isOpen) return null;

  const updateRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { stage: '', campaign_id: '' }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setError('');
    setWarning('');
    if (!isEdit && !apiKey.trim()) { setError('Follow Up Boss API key is required'); return; }

    const cleaned = rows
      .map((r) => ({ stage: (r.stage || '').trim(), campaign_id: r.campaign_id }))
      .filter((r) => r.stage && r.campaign_id);
    if (cleaned.length === 0) { setError('Add at least one stage → campaign mapping'); return; }

    const seen = new Set();
    for (const r of cleaned) {
      const key = r.stage.toLowerCase();
      if (seen.has(key)) { setError(`Duplicate stage: ${r.stage}`); return; }
      seen.add(key);
    }

    setSaving(true);
    try {
      const res = await onSave({ apiKey: apiKey.trim(), hotStage: hotStage.trim(), stageMappings: cleaned });
      if (res?.warning) setWarning(res.warning);
      else onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <span className="text-sm font-bold text-indigo-700">FUB</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Follow Up Boss Integration</h3>
              <p className="text-sm text-slate-500">Move a lead into a mapped stage to hand it to SurFox AI</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Downloadable quickstart guide (PDF) — lives in public/howto/ */}
          <a
            href="/howto/SurFox_FUB_Quickstart.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Follow Up Boss quickstart guide (PDF)</p>
                <p className="text-xs text-slate-500">Step-by-step: hand leads to the AI and get hot ones back in about 10 minutes</p>
              </div>
            </div>
            <span className="text-xs font-medium text-indigo-700 group-hover:underline flex-shrink-0 ml-3">Open</span>
          </a>

          {!hasApiKey && (
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                You don't have an active SurFox API key yet. Generate one in the <strong>API Keys</strong> section above so SurFox can register the Follow Up Boss webhook.
              </p>
            </div>
          )}

          {/* FUB API key */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Follow Up Boss API key {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={isEdit ? 'Leave blank to keep existing key' : 'Paste your FUB API key'}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Found in Follow Up Boss under Admin → API. Saving auto-registers the webhook.</p>
          </div>

          {/* Stage → campaign mappings */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">Stage → campaign mappings <span className="text-red-500">*</span></label>
              <button onClick={addRow} className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
                <Plus className="w-4 h-4" /><span>Add stage</span>
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              When a person is moved into one of these stages in Follow Up Boss, SurFox enrolls them in the matching campaign and starts the conversation. Stages not listed here are ignored.
            </p>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    value={r.stage}
                    onChange={(e) => updateRow(i, { stage: e.target.value })}
                    placeholder="FUB stage name (e.g., SurFox AI)"
                    className="col-span-5 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="col-span-1 text-center text-slate-400">→</span>
                  <select
                    value={r.campaign_id}
                    onChange={(e) => updateRow(i, { campaign_id: e.target.value })}
                    className="col-span-5 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Pick a campaign…</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button onClick={() => removeRow(i)} className="col-span-1 p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50" title="Remove">
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Hot-lead write-back stage */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hot-lead stage <span className="text-slate-400 font-normal">(optional)</span></label>
            <input
              type="text"
              value={hotStage}
              onChange={(e) => setHotStage(e.target.value)}
              placeholder="e.g., Hot Prospect (must match a stage in FUB)"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              When SurFox qualifies a lead as hot, it adds a note in FUB and (if set) moves the person into this stage.
            </p>
          </div>

          {error && (
            <div className="flex items-center space-x-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
            </div>
          )}
          {warning && (
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>Saved, but: {warning}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">{warning ? 'Close' : 'Cancel'}</button>
          <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center space-x-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{isEdit ? 'Save changes' : 'Connect Follow Up Boss'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ODOO MODAL — connect an Odoo instance, map CRM stages to SurFox campaigns
// ============================================================================
// Odoo routing is by STAGE (like FUB), but the connection is a JSON-RPC login
// (instance URL + database + username + API key). "Test connection" authenticates
// and loads the instance's CRM stages so mappings are picked from a dropdown
// rather than typed. Inbound is polled server-side (odoo-poll-leads); outbound
// hot-lead write-back goes through the Odoo API (stage update + chatter note).
const OdooModal = ({ isOpen, onClose, connection, campaigns, hasApiKey, onSave }) => {
  const isEdit = !!connection;
  const [baseUrl, setBaseUrl] = useState('');
  const [db, setDb] = useState('');
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [stages, setStages] = useState([]);            // [{ id, name }]
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [testOk, setTestOk] = useState(false);
  const [rows, setRows] = useState([{ stage_id: '', campaign_id: '' }]);
  const [hotStageId, setHotStageId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const creds = connection?.credentials || {};
    setBaseUrl(creds.base_url || '');
    setDb(creds.db || '');
    setUsername(creds.username || '');
    setApiKey('');
    setShowKey(false);
    setTesting(false);
    setTestError('');
    setTestOk(false);
    setError('');
    setWarning('');
    const cfg = connection?.config || {};
    setHotStageId(cfg.odoo_hot_stage_id != null ? String(cfg.odoo_hot_stage_id) : '');
    const existing = Array.isArray(cfg.odoo_stage_mappings) ? cfg.odoo_stage_mappings : [];
    setRows(existing.length
      ? existing.map((m) => ({ stage_id: m.stage_id != null ? String(m.stage_id) : '', campaign_id: m.campaign_id || '' }))
      : [{ stage_id: '', campaign_id: '' }]);
    // Seed the stage dropdown from stored mappings so the edit view shows names
    // before the customer re-tests the connection.
    setStages(existing.length
      ? existing.filter((m) => m.stage_id != null).map((m) => ({ id: Number(m.stage_id), name: m.stage_name || `Stage ${m.stage_id}` }))
      : []);
  }, [isOpen, connection]);

  if (!isOpen) return null;

  const stageName = (id) => {
    const hit = stages.find((s) => String(s.id) === String(id));
    return hit ? hit.name : '';
  };

  const updateRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { stage_id: '', campaign_id: '' }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleTest = async () => {
    setTestError('');
    setTestOk(false);
    if (!baseUrl.trim() || !db.trim() || !username.trim()) {
      setTestError('Enter your Odoo URL, database, and username first');
      return;
    }
    if (!apiKey.trim() && !isEdit) {
      setTestError('Enter your Odoo API key first');
      return;
    }
    setTesting(true);
    try {
      const res = await apiFetch('/api/integrations/odoo/test-connection', {
        method: 'POST',
        body: JSON.stringify({
          base_url: baseUrl.trim(),
          db: db.trim(),
          username: username.trim(),
          // On edit, fall back to the stored key when the field is left blank.
          api_key: apiKey.trim() || connection?.credentials?.api_key || '',
        }),
      });
      setStages(res.stages || []);
      setTestOk(true);
      if (!res.stages || res.stages.length === 0) {
        setTestError('Connected, but no CRM stages were found in this Odoo instance.');
      }
    } catch (err) {
      setTestError(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setWarning('');
    if (!isEdit && (!baseUrl.trim() || !db.trim() || !username.trim() || !apiKey.trim())) {
      setError('Odoo URL, database, username, and API key are all required');
      return;
    }

    const cleaned = rows
      .map((r) => ({ stage_id: r.stage_id, campaign_id: r.campaign_id }))
      .filter((r) => r.stage_id && r.campaign_id)
      .map((r) => ({ stage_id: Number(r.stage_id), stage_name: stageName(r.stage_id), campaign_id: r.campaign_id }));
    if (cleaned.length === 0) { setError('Add at least one stage → campaign mapping'); return; }

    const seen = new Set();
    for (const r of cleaned) {
      if (seen.has(r.stage_id)) { setError(`Duplicate stage: ${r.stage_name || r.stage_id}`); return; }
      seen.add(r.stage_id);
    }

    setSaving(true);
    try {
      const res = await onSave({
        baseUrl: baseUrl.trim(),
        db: db.trim(),
        username: username.trim(),
        apiKey: apiKey.trim(),
        stageMappings: cleaned,
        hotStageId: hotStageId ? Number(hotStageId) : null,
        hotStageName: hotStageId ? stageName(hotStageId) : null,
      });
      if (res?.warning) setWarning(res.warning);
      else onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <span className="text-sm font-bold text-purple-700">Od</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Odoo Integration</h3>
              <p className="text-sm text-slate-500">Move a lead into a mapped Odoo stage to hand it to SurFox AI</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Downloadable quickstart guide (PDF) — lives in public/howto/ */}
          <a
            href="/howto/SurFox_Odoo_Quickstart.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-purple-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">Odoo quickstart guide (PDF)</p>
                <p className="text-xs text-slate-500">Step-by-step: connect Odoo and sync leads both ways in about 5 minutes</p>
              </div>
            </div>
            <span className="text-xs font-medium text-purple-700 group-hover:underline flex-shrink-0 ml-3">Open</span>
          </a>

          {!hasApiKey && (
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                You don't have an active SurFox API key yet. Generate one in the <strong>API Keys</strong> section above so SurFox can pull leads from Odoo.
              </p>
            </div>
          )}

          <div className="flex items-start space-x-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600">
              Odoo's API is available on <strong>Custom</strong> Odoo plans (not One App Free or Standard). Create an API key in Odoo under your user <strong>Preferences → Account Security → New API Key</strong>.
            </p>
          </div>

          {/* Connection fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Odoo URL {!isEdit && <span className="text-red-500">*</span>}</label>
              <input type="text" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://yourcompany.odoo.com" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Database {!isEdit && <span className="text-red-500">*</span>}</label>
              <input type="text" value={db} onChange={(e) => setDb(e.target.value)} placeholder="database name" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Username {!isEdit && <span className="text-red-500">*</span>}</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@company.com" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">API key {!isEdit && <span className="text-red-500">*</span>}</label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isEdit ? 'Leave blank to keep existing key' : 'Paste your Odoo API key'}
                  className={`${inputCls} pr-10 font-mono`}
                />
                <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Test connection */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleTest}
              disabled={testing}
              className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              {testing && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>Test connection &amp; load stages</span>
            </button>
            {testOk && stages.length > 0 && (
              <span className="inline-flex items-center text-sm text-emerald-700">
                <CheckCircle className="w-4 h-4 mr-1" /> {stages.length} stage{stages.length !== 1 ? 's' : ''} loaded
              </span>
            )}
          </div>
          {testError && (
            <div className="flex items-start space-x-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{testError}</span>
            </div>
          )}

          {/* Stage → campaign mappings */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">Stage → campaign mappings <span className="text-red-500">*</span></label>
              <button onClick={addRow} disabled={stages.length === 0} className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50">
                <Plus className="w-4 h-4" /><span>Add stage</span>
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              When a lead enters one of these Odoo stages, SurFox enrolls it in the matching campaign and starts the conversation. Stages not listed here are ignored. Test the connection above to load your stages.
            </p>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    value={r.stage_id}
                    onChange={(e) => updateRow(i, { stage_id: e.target.value })}
                    className="col-span-5 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Pick an Odoo stage…</option>
                    {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <span className="col-span-1 text-center text-slate-400">→</span>
                  <select
                    value={r.campaign_id}
                    onChange={(e) => updateRow(i, { campaign_id: e.target.value })}
                    className="col-span-5 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Pick a campaign…</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button onClick={() => removeRow(i)} className="col-span-1 p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50" title="Remove">
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Hot-lead write-back stage */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hot-lead stage <span className="text-slate-400 font-normal">(optional)</span></label>
            <select
              value={hotStageId}
              onChange={(e) => setHotStageId(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Don't change the stage</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              When SurFox qualifies a lead as hot, it posts a note in Odoo and (if set) moves the lead into this stage.
            </p>
          </div>

          {error && (
            <div className="flex items-center space-x-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
            </div>
          )}
          {warning && (
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>Saved, but: {warning}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex items-center justify-end space-x-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">{warning ? 'Close' : 'Cancel'}</button>
          <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 flex items-center space-x-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{isEdit ? 'Save changes' : 'Connect Odoo'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function IntegrationsSection({ onUpdate }) {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showZapierEmbed, setShowZapierEmbed] = useState(false);
  const [showZapierSetup, setShowZapierSetup] = useState(false);
  const [showRecruiterFlowSetup, setShowRecruiterFlowSetup] = useState(false);
  const [showManageZapier, setShowManageZapier] = useState(false);
  const [showManageRecruiterFlow, setShowManageRecruiterFlow] = useState(false);
  const [showRecruiterFlowImport, setShowRecruiterFlowImport] = useState(false);
  const [showHighLevel, setShowHighLevel] = useState(false);
  const [showFub, setShowFub] = useState(false);
  const [showOdoo, setShowOdoo] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [hasApiKey, setHasApiKey] = useState(false);

  const inboundUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inbound-leads`;

  // Fetch existing connections + HighLevel-related data
  useEffect(() => {
    if (user?.tenant_id) {
      fetchConnections();
      fetchMappings();
      fetchCampaigns();
      fetchApiKeyStatus();
    }
  }, [user?.tenant_id]);

  const fetchConnections = async () => {
    try {
      const { data, error } = await supabase
        .from('integration_connections')
        .select('*')
        .eq('tenant_id', user.tenant_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConnections(data || []);
    } catch (err) {
      console.error('Error fetching integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Zapier handlers
  const handleSaveZapier = async (connectionData) => {
    const { data, error } = await supabase
      .from('integration_connections')
      .insert({
        tenant_id: user.tenant_id,
        integration_type: 'zapier',
        integration_name: connectionData.integration_name,
        webhook_url: connectionData.webhook_url,
        subscribed_events: connectionData.subscribed_events,
        enabled: true
      })
      .select()
      .single();

    if (error) throw error;

    // Send test event
    try {
      await fetch(connectionData.webhook_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'test',
          message: 'SurFox connection successful!',
          timestamp: new Date().toISOString(),
          sample_lead: {
            lead_id: 'test-123',
            phone: '+15551234567',
            first_name: 'Test',
            last_name: 'Lead',
            status: 'hot'
          }
        })
      });
    } catch (testError) {
      console.warn('Test webhook failed (may be expected):', testError);
    }

    await fetchConnections();
    onUpdate?.();
  };

  // RecruiterFlow handlers
  const handleSaveRecruiterFlow = async (connectionData) => {
    const existingConnection = connections.find(c => c.integration_type === 'recruiterflow');
    
    if (existingConnection) {
      // Update existing
      const { error } = await supabase
        .from('integration_connections')
        .update({
          integration_name: connectionData.integration_name,
          credentials: { api_key: connectionData.api_key },
          subscribed_events: ['lead_turned_hot'],
          enabled: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingConnection.id);

      if (error) throw error;
    } else {
      // Create new
      const { error } = await supabase
        .from('integration_connections')
        .insert({
          tenant_id: user.tenant_id,
          integration_type: 'recruiterflow',
          integration_name: connectionData.integration_name,
          credentials: { api_key: connectionData.api_key },
          subscribed_events: ['lead_turned_hot'],
          enabled: true
        });

      if (error) throw error;
    }

    await fetchConnections();
    onUpdate?.();
  };

  // Generic handlers
  const handleDeleteConnection = async (connectionId) => {
    const { error } = await supabase
      .from('integration_connections')
      .delete()
      .eq('id', connectionId);

    if (error) throw error;
    await fetchConnections();
    onUpdate?.();
  };

  const handleToggleConnection = async (connectionId, enabled) => {
    const { error } = await supabase
      .from('integration_connections')
      .update({ enabled })
      .eq('id', connectionId);

    if (error) throw error;
    await fetchConnections();
  };

  // ---- HighLevel: fetches and CRUD ----

  const fetchMappings = async () => {
    const { data, error } = await supabase
      .from('webhook_tag_mappings')
      .select('id, tag, campaign_id, is_active, created_at, updated_at')
      .order('is_active', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching webhook tag mappings:', error);
      return;
    }
    setMappings(data || []);
  };

  const fetchCampaigns = async () => {
    const { data, error } = await supabase
      .from('campaigns')
      .select('id, name')
      .eq('tenant_id', user.tenant_id)
      .order('name');
    if (error) {
      console.error('Error fetching campaigns:', error);
      return;
    }
    setCampaigns(data || []);
  };

  // Asks the server (api-keys-admin), tenant-scoped by the caller's session; the
  // browser no longer reads zapier_api_keys directly.
  const fetchApiKeyStatus = async () => {
    const { data, error } = await supabase.functions.invoke('api-keys-admin', {
      body: { action: 'list' },
    });
    if (error || data?.error) {
      console.error('Error checking API keys:', error || data.error);
      return;
    }
    // FUB/Odoo/Zapier need a Full access key that has not expired; a read-only
    // key cannot create leads or subscribe hooks.
    setHasApiKey((data?.keys || []).some((k) =>
      k.is_active && (k.scope || 'full') === 'full' &&
      !(k.expires_at && new Date(k.expires_at).getTime() <= Date.now())));
  };

  const handleAddMapping = async (tag, campaignId) => {
    const { error } = await supabase.from('webhook_tag_mappings').insert({
      tenant_id: user.tenant_id,
      tag: tag.trim(),
      campaign_id: campaignId,
      is_active: true,
    });
    if (error) {
      if (error.code === '23505') {
        return { error: 'This tag is already mapped. Remove the existing mapping first.' };
      }
      return { error: error.message || 'Failed to add mapping' };
    }
    await fetchMappings();
    onUpdate?.();
    return { error: null };
  };

  const handleUpdateMappingCampaign = async (mappingId, campaignId) => {
    const { error } = await supabase
      .from('webhook_tag_mappings')
      .update({ campaign_id: campaignId })
      .eq('id', mappingId);
    if (error) {
      console.error('Error updating mapping:', error);
      return;
    }
    await fetchMappings();
  };

  const handleSoftDeleteMapping = async (mappingId) => {
    const { error } = await supabase
      .from('webhook_tag_mappings')
      .update({ is_active: false })
      .eq('id', mappingId);
    if (error) {
      console.error('Error removing mapping:', error);
      return;
    }
    await fetchMappings();
    onUpdate?.();
  };

  // Connection status checks
  const zapierConnections = connections.filter(c => c.integration_type === 'zapier');
  const hasZapier = zapierConnections.length > 0;

  const recruiterFlowConnection = connections.find(c => c.integration_type === 'recruiterflow');
  const hasRecruiterFlow = !!recruiterFlowConnection;

  const fubConnection = connections.find(c => c.integration_type === 'follow_up_boss');
  const hasFub = !!fubConnection;

  // Save (create or update) the Follow Up Boss connection via /api/integrations
  // so the backend auto-registers/updates the FUB webhook. Returns the response
  // (which may include a { warning } when registration was skipped/failed).
  const handleSaveFub = async ({ apiKey, hotStage, stageMappings }) => {
    const config = { fub_stage_mappings: stageMappings, fub_hot_stage: hotStage };
    let res;
    if (fubConnection) {
      const body = { config };
      if (apiKey) body.credentials = { auth_type: 'fub', api_key: apiKey };
      res = await apiFetch(`/api/integrations/${fubConnection.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    } else {
      res = await apiFetch('/api/integrations', {
        method: 'POST',
        body: JSON.stringify({
          integration_type: 'follow_up_boss',
          integration_name: 'Follow Up Boss',
          subscribed_events: ['lead_turned_hot'],
          credentials: { auth_type: 'fub', api_key: apiKey },
          config,
        }),
      });
    }
    await fetchConnections();
    onUpdate?.();
    return res;
  };

  const odooConnection = connections.find(c => c.integration_type === 'odoo');
  const hasOdoo = !!odooConnection;

  // Save (create or update) the Odoo connection via /api/integrations. On edit
  // with a blank API key field we resend the stored key (validation requires it)
  // since the browser already has same-tenant read access to it.
  const handleSaveOdoo = async ({ baseUrl, db, username, apiKey, stageMappings, hotStageId, hotStageName }) => {
    const config = { odoo_stage_mappings: stageMappings, odoo_hot_stage_id: hotStageId, odoo_hot_stage_name: hotStageName };
    let res;
    if (odooConnection) {
      const credentials = { auth_type: 'odoo', base_url: baseUrl, db, username, api_key: apiKey || odooConnection.credentials?.api_key };
      res = await apiFetch(`/api/integrations/${odooConnection.id}`, { method: 'PATCH', body: JSON.stringify({ credentials, config }) });
    } else {
      res = await apiFetch('/api/integrations', {
        method: 'POST',
        body: JSON.stringify({
          integration_type: 'odoo',
          integration_name: 'Odoo',
          subscribed_events: ['lead_turned_hot'],
          credentials: { auth_type: 'odoo', base_url: baseUrl, db, username, api_key: apiKey },
          config,
        }),
      });
    }
    await fetchConnections();
    onUpdate?.();
    return res;
  };

  const activeMappingCount = mappings.filter((m) => m.is_active).length;
  const hasHighLevel = activeMappingCount > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* API Key Management */}
      <ApiKeyManager onUpdate={onUpdate} />

      {/* Info Banner */}
      <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl">
        <div className="flex items-start space-x-3">
          <Plug className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-indigo-900">Connect your CRM</h4>
            <p className="text-sm text-indigo-700 mt-1">
              When leads turn hot, automatically push them to your CRM, trigger workflows, or notify your team through 5,000+ apps.
            </p>
          </div>
        </div>
      </div>

      {/* Integration Cards */}
      <div className="space-y-3">
        {/* Zapier - Expandable Workflow Embed */}
        <div className={`rounded-xl border overflow-hidden transition-colors ${showZapierEmbed ? 'border-orange-200' : 'border-slate-200 hover:border-slate-300'}`}>
          {/* Card Header */}
          <div className="bg-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h4 className="font-semibold text-slate-900">Zapier</h4>
                  {hasZapier && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connected
                    </span>
                  )}
                  {/* Works with Zapier badge */}
                  <a
                    href="https://zapier.com/apps/surfox/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:inline-flex items-center space-x-1 px-2 py-0.5 bg-white border border-slate-200 rounded-md hover:border-slate-300 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" aria-hidden="true">
                      <path fill="#FF4A00" d="M15.637 8.363h-3.273V5.09A4.364 4.364 0 0 0 8 .727h-.727A4.364 4.364 0 0 0 2.91 5.09v3.273H0v7.274h2.909v3.272A4.364 4.364 0 0 0 7.273 23.273H8a4.364 4.364 0 0 0 4.364-4.364v-3.272h3.273l3.636-3.637-3.636-3.637zm-3.273 3.637H8.364V8.363h4V12zm7.636 0h-3.636V8.363H20V5.09a4.364 4.364 0 0 1 4 4.364v.908a4.364 4.364 0 0 1-4 4.364V12z" />
                    </svg>
                    <span className="text-xs font-medium text-slate-600">Works with Zapier</span>
                  </a>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">Connect to 5,000+ apps including CRMs, spreadsheets, and more</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-shrink-0">
              {hasZapier && !showZapierEmbed && (
                <button
                  onClick={() => setShowManageZapier(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-slate-600 bg-slate-100 hover:bg-slate-200"
                >
                  Manage
                </button>
              )}
              <button
                onClick={() => setShowZapierEmbed(!showZapierEmbed)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5
                  ${showZapierEmbed
                    ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                    : 'text-white bg-slate-900 hover:bg-slate-800'
                  }
                `}
              >
                <span>{showZapierEmbed ? 'Hide' : (hasZapier ? 'Add Zap' : 'Connect')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showZapierEmbed ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Expandable Embed Section */}
          {showZapierEmbed && (
            <div className="border-t border-orange-100 bg-slate-50 p-4">
              <ZapierWorkflowEmbed
                userId={user?.id}
                fallbackEmail={user?.email}
              />
            </div>
          )}
        </div>

        {/* Follow Up Boss - Stage routing */}
        <IntegrationCard
          name="Follow Up Boss"
          logo={<span className="text-sm font-bold text-indigo-700">FUB</span>}
          description="Move a lead into a mapped FUB stage to hand it to SurFox AI"
          status={hasFub ? 'connected' : null}
          onConnect={() => setShowFub(true)}
          onManage={() => setShowFub(true)}
        />

        {/* HighLevel - Webhook tag routing */}
        <IntegrationCard
          name="HighLevel"
          logo={<span className="text-base font-bold text-teal-700">HL</span>}
          description="Route HighLevel Workflow leads to SurFox campaigns by tag"
          status={hasHighLevel ? 'connected' : null}
          onConnect={() => setShowHighLevel(true)}
          onManage={() => setShowHighLevel(true)}
        />

        {/* Odoo - Stage routing (polled inbound + API write-back) */}
        <IntegrationCard
          name="Odoo"
          logo={<span className="text-sm font-bold text-purple-700">Od</span>}
          description="Sync Odoo CRM leads to SurFox by stage, with hot-lead write-back"
          status={hasOdoo ? 'connected' : null}
          onConnect={() => setShowOdoo(true)}
          onManage={() => setShowOdoo(true)}
        />

        {/* RecruiterFlow - Native Integration */}
        <div className="bg-white rounded-xl border p-4 flex items-center justify-between border-slate-200 hover:border-slate-300 transition-colors">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-semibold text-slate-900">RecruiterFlow</h4>
                {hasRecruiterFlow && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Connected
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Native ATS integration - sync hot leads as candidates</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {hasRecruiterFlow && (
              <button
                onClick={() => setShowRecruiterFlowImport(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-sky-600 bg-sky-50 hover:bg-sky-100"
              >
                Import
              </button>
            )}
            <button
              onClick={hasRecruiterFlow ? () => setShowManageRecruiterFlow(true) : () => setShowRecruiterFlowSetup(true)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0
                ${hasRecruiterFlow
                  ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                  : 'text-white bg-slate-900 hover:bg-slate-800'
                }
              `}
            >
              {hasRecruiterFlow ? 'Manage' : 'Connect'}
            </button>
          </div>
        </div>

        {/* Bullhorn - Coming Soon */}
        <IntegrationCard
          name="Bullhorn"
          logo={<span className="text-lg font-bold text-red-600">B</span>}
          description="Native ATS integration for staffing firms"
          comingSoon
        />

        {/* HubSpot - Coming Soon */}
        <IntegrationCard
          name="HubSpot"
          logo={<span className="text-lg font-bold text-orange-500">H</span>}
          description="Sync leads and activities with HubSpot CRM"
          comingSoon
        />

        {/* Salesforce - Coming Soon */}
        <IntegrationCard
          name="Salesforce"
          logo={<span className="text-lg font-bold text-blue-600">SF</span>}
          description="Push qualified leads directly to Salesforce"
          comingSoon
        />
      </div>

      {/* Connection count */}
      {connections.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            {connections.filter(c => c.enabled).length} active connection{connections.filter(c => c.enabled).length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Modals */}
      <ZapierSetupModal
        isOpen={showZapierSetup}
        onClose={() => setShowZapierSetup(false)}
        onSave={handleSaveZapier}
        existingConnections={zapierConnections}
      />

      <RecruiterFlowSetupModal
        isOpen={showRecruiterFlowSetup}
        onClose={() => setShowRecruiterFlowSetup(false)}
        onSave={handleSaveRecruiterFlow}
        existingConnection={recruiterFlowConnection}
      />

      <ManageConnectionsModal
        isOpen={showManageZapier}
        onClose={() => setShowManageZapier(false)}
        connections={connections}
        onDelete={handleDeleteConnection}
        onToggle={handleToggleConnection}
        integrationType="zapier"
        title="Manage Zapier Connections"
      />

      <ManageConnectionsModal
        isOpen={showManageRecruiterFlow}
        onClose={() => setShowManageRecruiterFlow(false)}
        connections={connections}
        onDelete={handleDeleteConnection}
        onToggle={handleToggleConnection}
        integrationType="recruiterflow"
        title="Manage RecruiterFlow Connection"
      />
      <RecruiterFlowImport
        isOpen={showRecruiterFlowImport}
        onClose={() => setShowRecruiterFlowImport(false)}
        onImportComplete={() => {
          // Optionally show a success message or refresh data
          console.log('Import complete');
        }}
      />

      <HighLevelMappingsModal
        isOpen={showHighLevel}
        onClose={() => setShowHighLevel(false)}
        mappings={mappings}
        campaigns={campaigns}
        hasApiKey={hasApiKey}
        inboundUrl={inboundUrl}
        onAdd={handleAddMapping}
        onUpdateCampaign={handleUpdateMappingCampaign}
        onSoftDelete={handleSoftDeleteMapping}
      />

      <FollowUpBossModal
        isOpen={showFub}
        onClose={() => setShowFub(false)}
        connection={fubConnection}
        campaigns={campaigns}
        hasApiKey={hasApiKey}
        onSave={handleSaveFub}
      />

      <OdooModal
        isOpen={showOdoo}
        onClose={() => setShowOdoo(false)}
        connection={odooConnection}
        campaigns={campaigns}
        hasApiKey={hasApiKey}
        onSave={handleSaveOdoo}
      />
    </div>
  );
}